import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { FormsModule } from "@angular/forms";
import { MatCardModule } from "@angular/material/card";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { DataLoaderComponent } from "../../shared/data-loader/data-loader.component";
import { ProductionRequestService } from "../../services/production-request.service";
import { NotificationService } from "../../services/production-request.service";
import { UploadService } from "../../services/upload.service";
import { PermissionService } from "../../services/permission.service";
import { ProfileService } from "../../services/profile.service";
import { PageHeaderComponent } from "../../shared/page-header/page-header.component";
import {
  ProductionRequestDetail,
  ProductionRequestMessage,
  ProductionRequestAudioVersion,
  PRODUCTION_REQUEST_STATUS_LABELS,
  PRODUCTION_REQUEST_STATUS_CLASS,
  ProductionRequestStatus,
} from "../../models/production-request.model";
import { DeclineModalComponent } from "./decline-modal.component";
import { ApproveModalComponent } from "./approve-modal.component";
import { SnackbarService } from "../../services/snackbar.service";
import { COMMERCIAL_AUDIO_ERROR, isCommercialAudioFile } from "../../utils/audio-file";
import { ProductTypeService } from "../../services/product-type.service";
import { ProductType } from "../../models/product-type.model";
import { forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";

@Component({
  selector: "app-production-request-detail",
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    DataLoaderComponent,
    PageHeaderComponent,
  ],
  templateUrl: "./production-request-detail.component.html",
  styleUrls: ["./production-request-detail.component.css"],
})
export class ProductionRequestDetailComponent implements OnInit {
  id: number | null = null;
  detail: ProductionRequestDetail | null = null;
  loading = false;
  error = "";
  successMessage = "";
  replyMessage = "";
  sendingMessage = false;
  requestMoreInfoText = "";
  sendingMoreInfo = false;
  isUserRole = false;
  isStationRole = false;
  showCreateCommercialButton = false;
  isApproved = false;
  audioVersionProductTypes: Record<number, ProductType[]> = {};

  /** Only station roles can create a new version when status is not audio_uploaded (waiting for approval). */
  canCreateCommercial(): boolean {
    if (!this.showCreateCommercialButton || this.isApproved || !this.detail?.request) return false;
    return this.detail.request.status !== "audio_uploaded";
  }
  statusLabels = PRODUCTION_REQUEST_STATUS_LABELS;
  statusClass = PRODUCTION_REQUEST_STATUS_CLASS;

  // Upload / record (station)
  uploadFile: File | null = null;
  uploadTmpKey: string | null = null;
  isUploading = false;
  uploadProgress = 0;
  isRecording = false;
  recordingBlob: Blob | null = null;
  recordingUrl: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private productionRequestService: ProductionRequestService,
    private notificationService: NotificationService,
    private uploadService: UploadService,
    private permissionService: PermissionService,
    private profileService: ProfileService,
    private dialog: MatDialog,
    private snackbar: SnackbarService,
    private productTypeService: ProductTypeService,
  ) { }

  ngOnInit(): void {
    const role = this.permissionService.getCurrentRole();
    this.isUserRole = role === "user";
    this.isStationRole = ["admin", "owner", "station_owner", "station_user"].includes(role || "");
    const idParam = this.route.snapshot.paramMap.get("id");
    if (idParam) {
      this.id = +idParam;
      this.load();
    }
  }

  /**
   * Customer vs station controls for THIS request: whether the caller placed it, and their
   * role on the request's station -- not the active station's role (backend:
   * is_production_requester / station_role_for_request).
   */
  private applyRequestRoles(request: { user_id?: number; station_id?: number } | null | undefined): void {
    const stationRole = this.permissionService.roleForStation(request?.station_id);
    const isStaffHere = ["admin", "owner", "station_owner", "station_user"].includes(stationRole);
    const me = this.profileService.getCurrentUser()?.userid;
    this.isStationRole = isStaffHere;
    this.showCreateCommercialButton = isStaffHere;
    this.isUserRole = me != null && request?.user_id != null
      ? Number(request.user_id) === Number(me)
      : !isStaffHere;
  }

  load(): void {
    if (this.id == null) return;
    this.loading = true;
    this.error = "";
    this.productionRequestService.getById(this.id).subscribe({
      next: (data) => {
        this.detail = data;
        this.isApproved = data?.request?.status === "approved";
        this.applyRequestRoles(data?.request);
        this.loading = false;
        this.loadAudioVersionProductTypes(data?.audio_versions ?? []);
        if (this.id) {
          this.notificationService.markReadByReference(this.id).subscribe({ error: () => {} });
        }
      },
      error: (err) => {
        const msg = err?.error?.error || "Something went wrong. Please try again.";
        this.error = msg;
        this.snackbar.error(msg);
        this.loading = false;
      },
    });
  }

  getStatusClass(status: ProductionRequestStatus): string {
    return this.statusClass[status] || "status-pending";
  }

  isFromUser(msg: ProductionRequestMessage): boolean {
    return msg.sender_role === "user";
  }

  get messages(): ProductionRequestMessage[] {
    return this.detail?.messages ?? [];
  }

  /** Only admin/owner/station can ask; user cannot send first message. User can only reply when last message is from station. */
  canUserReply(): boolean {
    if (!this.isUserRole || this.isApproved || !this.detail?.messages?.length) return false;
    const last = this.detail.messages[this.detail.messages.length - 1];
    return last.sender_role !== "user";
  }

  requestMoreInfo(): void {
    if (!this.id || !this.requestMoreInfoText.trim() || this.isApproved) return;
    this.sendingMoreInfo = true;
    this.productionRequestService.askMoreInfo(this.id, this.requestMoreInfoText.trim()).subscribe({
      next: () => {
        this.requestMoreInfoText = "";
        this.sendingMoreInfo = false;
        this.successMessage = "Request for more information sent.";
        this.load();
      },
      error: (err) => {
        this.sendingMoreInfo = false;
        this.error = err?.error?.error || "Failed to send request.";
      },
    });
  }

  postReply(): void {
    if (!this.id || !this.replyMessage.trim() || !this.canUserReply()) return;
    this.sendingMessage = true;
    this.productionRequestService.postMessage(this.id, this.replyMessage.trim()).subscribe({
      next: () => {
        this.replyMessage = "";
        this.sendingMessage = false;
        this.successMessage = "Reply sent.";
        this.snackbar.success("Reply sent.");
        this.load();
      },
      error: (err) => {
        this.sendingMessage = false;
        const msg = err?.error?.error || "Failed to send reply.";
        this.error = msg;
        this.snackbar.error(msg);
      },
    });
  }

  openApproveModal(av: ProductionRequestAudioVersion): void {
    if (!this.id || !this.detail?.request) return;
    const req = this.detail.request;
    const preselectedProductTypeIds = (this.audioVersionProductTypes[av.id] ?? []).map((pt) => pt.id);
    const ref = this.dialog.open(ApproveModalComponent, {
      width: "480px",
      panelClass: "app-confirm-dialog",
      data: {
        stationId: req.station_id,
        stationName: req.station_name || `Station #${req.station_id}`,
        preselectedProductTypeIds,
      },
    });
    ref.afterClosed().subscribe((result: {
      recording_name: string
      station_id: number
      product_type_ids: number[]
    } | undefined) => {
      if (result && this.id) {
        this.productionRequestService.approve(this.id, {
          ...result,
          audio_id: av.id,
          version: av.version,
        }).subscribe({
          next: () => {
            this.successMessage = "Commercial approved successfully.";
            this.snackbar.success("Commercial approved successfully.");
            this.router.navigate(["/production-requests"]);
          },
          error: (err) => {
            const msg = err?.error?.error || "Failed to approve.";
            this.error = msg;
            this.snackbar.error(msg);
          },
        });
      }
    });
  }

  openDeclineModal(av: ProductionRequestAudioVersion): void {
    const ref = this.dialog.open(DeclineModalComponent, {
      width: "480px",
      panelClass: "app-confirm-dialog",
      data: {},
    });
    ref.afterClosed().subscribe((result: { missing_information: string; explanation: string } | undefined) => {
      if (result && this.id) {
        this.productionRequestService
          .decline(this.id, {
            explanation: result.explanation || "",
            missing_information: result.missing_information || "",
            audio_id: av.id,
            version: av.version,
          })
          .subscribe({
            next: () => {
              this.successMessage = "Feedback sent. Station will review.";
              this.snackbar.success("Feedback sent. The user will be notified.");
              this.load();
            },
            error: (err) => {
              const msg = err?.error?.error || "Failed to submit feedback.";
              this.error = msg;
              this.snackbar.error(msg);
            },
          });
      }
    });
  }

  onUploadFileChange(e: Event): void {
    const input = e.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    if (!isCommercialAudioFile(file)) {
      input.value = "";
      this.uploadFile = null;
      this.snackbar.warning(COMMERCIAL_AUDIO_ERROR);
      return;
    }
    this.uploadFile = file;
    this.startUpload(file);
  }

  startUpload(file: File): void {
    if (!this.id) return;

    this.isUploading = true;
    this.uploadProgress = 0;

    // generate_url requires request_id + version for production_requests and builds
    // production-requests/{id}/{uuid}/v{version}.{ext}; post_commercial_request_audio parses
    // the version back out of that key. Same source as create-commercial: the detail API's
    // next_audio_version (latest registered version + 1).
    const version = this.detail?.next_audio_version ?? 1;

    this.uploadService
      .uploadFile(file, "production_requests", { request_id: this.id, version })
      .subscribe({
        next: (result) => {

          this.productionRequestService
            .registerAudio(this.id!, result.tmp_key)
            .subscribe({

              next: () => {
                this.isUploading = false;
                this.uploadFile = null;
                this.successMessage = "Audio uploaded successfully.";
                this.snackbar.success("Audio uploaded successfully.");
                this.load();
              },

              error: (err) => {
                this.isUploading = false;
                const msg = err?.error?.error || "Failed to register audio.";
                this.error = msg;
                this.snackbar.error(msg);
              },
            });
        },

        error: (err) => {
          this.isUploading = false;
          const msg =
            err?.status === 403
              ? "You don't have permission to upload audio for this request."
              : err?.status === 404
                ? "This production request no longer exists."
                : err?.error?.error || "Upload failed. Please try again.";
          this.error = msg;
          this.snackbar.error(msg);
        },
      });
  }

  triggerFileInput(): void {
    (document.getElementById("production-audio-file") as HTMLInputElement)?.click();
  }

  startRecording(): void {
    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        this.recordingBlob = new Blob(chunks, { type: "audio/webm" });
        this.recordingUrl = URL.createObjectURL(this.recordingBlob);
        this.isRecording = false;
      };
      recorder.start();
      (this as any)._recorder = recorder;
      this.isRecording = true;
    }).catch(() => {
      this.error = "Microphone access is required to record.";
    });
  }

  stopRecording(): void {
    const rec = (this as any)._recorder;
    if (rec && rec.state !== "inactive") rec.stop();
  }

  submitRecording(): void {
    if (!this.recordingBlob || !this.id) return;
    const file = new File([this.recordingBlob], "recording.webm", { type: "audio/webm" });
    this.startUpload(file);
    this.recordingBlob = null;
    if (this.recordingUrl) {
      URL.revokeObjectURL(this.recordingUrl);
      this.recordingUrl = null;
    }
  }

  clearRecording(): void {
    this.recordingBlob = null;
    if (this.recordingUrl) {
      URL.revokeObjectURL(this.recordingUrl);
      this.recordingUrl = null;
    }
  }

  helpWithCommercialRequest(): void {
    const crId = this.detail?.request?.id
    if (!crId) return
    const stationName = this.detail?.request?.station_name || ""

    this.router.navigate(["/help"], {
      queryParams: {
        prefill: "true",
        category: "commercial_production",
        reference_type: "commercial_request",
        reference_id: crId,
        subject: `Issue with Commercial Request #${crId}`,
        reference_label: `Commercial Request #${crId} — ${stationName}`,
      },
    })
  }

  canApproveVersion(av: ProductionRequestAudioVersion): boolean {
    if (!this.detail?.request || this.isApproved) return false;
    const status = av.status || "waiting_review";
    if (status !== "waiting_review") return false;
    return this.isUserRole;
  }

  canDeclineVersion(av: ProductionRequestAudioVersion): boolean {
    if (!this.isUserRole || !this.detail?.request || this.isApproved) return false;
    const status = av.status || "waiting_review";
    return status === "waiting_review";
  }

  getAudioVersionStatusLabel(status: string | undefined): string {
    const s = status || "waiting_review";
    if (s === "waiting_review") return "In Review";
    if (s === "approved") return "Approved";
    if (s === "declined") return "Declined";
    return s;
  }

  getAudioVersionStatusClass(status: string | undefined): string {
    const s = status || "waiting_review";
    if (s === "approved") return "chip-approved";
    if (s === "declined") return "chip-declined";
    return "chip-in-review";
  }

  productTypeLabel(pt: ProductType): string {
    return `[${pt.name}] ${pt.description}`;
  }

  private loadAudioVersionProductTypes(
    versions: ProductionRequestAudioVersion[],
  ): void {
    this.audioVersionProductTypes = {};
    if (!versions.length) return;

    forkJoin(
      versions.map((av) =>
        this.productTypeService.getProductionRequestAudioProductTypes(av.id).pipe(
          catchError(() => of([] as ProductType[])),
        ),
      ),
    ).subscribe({
      next: (results) => {
        versions.forEach((av, index) => {
          const types = results[index] ?? [];
          if (types.length) {
            this.audioVersionProductTypes[av.id] = types;
          }
        });
      },
      error: () => {},
    });
  }
}
